/**
 * OnboardingRevenueStep: the wizard's exit, after the tag is verified or
 * skipped (Paper "Onboarding · 05 Revenue").
 *
 * Offers the three connectors that turn tests into money: Stripe for revenue,
 * Meta and Google Ads for spend. Paper's "found on acme.com" pills and the
 * "We found Stripe…" line are not built: nothing detects these tools on the
 * site yet, so the subhead stays neutral. A connected row shows the status
 * pill only; there are no revenue figures here until a sync has run.
 *
 * Presentational. The page owns the connection state and the connect actions.
 */

import { BrandIcon } from "@/components/ui/brand-icons"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export type RevenueConnectorId = "stripe" | "meta" | "google-ads"

export type RevenueConnectorRow = {
  id: RevenueConnectorId
  /** null while the status is still loading. */
  connected: boolean | null
}

const CONNECTOR_META: Record<
  RevenueConnectorId,
  { name: string; brand: "stripe" | "meta" | "googleAds"; detail: string }
> = {
  stripe: { name: "Stripe", brand: "stripe", detail: "Scores each test in revenue, not clicks" },
  meta: { name: "Meta Ads", brand: "meta", detail: "Pulls 30 days of spend by campaign" },
  "google-ads": { name: "Google Ads", brand: "googleAds", detail: "Pulls 30 days of spend by campaign" },
}

function ConnectedPill() {
  return (
    <span className="inline-flex h-[22px] shrink-0 items-center gap-1.5 rounded-md bg-muted px-2 text-xs leading-4 text-ui-ink">
      <span aria-hidden className="size-1.5 rounded-full bg-live" />
      Connected
    </span>
  )
}

type OnboardingRevenueStepProps = {
  connectors: RevenueConnectorRow[]
  /** The row whose connect action is in flight. */
  connectingId?: RevenueConnectorId | null
  onConnect: (id: RevenueConnectorId) => void
  /** Primary exit. Names the paywall when the account has no billing yet. */
  continueLabel: string
  onContinue: () => void
  onLater: () => void
  className?: string
}

export function OnboardingRevenueStep({
  connectors,
  connectingId,
  onConnect,
  continueLabel,
  onContinue,
  onLater,
  className,
}: OnboardingRevenueStepProps) {
  return (
    <section aria-labelledby="onboarding-revenue-heading" className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-col gap-2">
        <h1
          id="onboarding-revenue-heading"
          className="text-[22px] font-semibold leading-7 tracking-[-0.02em] text-foreground sm:text-[28px] sm:leading-[34px]"
        >
          Connect where the money is
        </h1>
        <p className="text-sm leading-5 text-muted-foreground sm:text-base sm:leading-6">
          Connect Stripe and your ad accounts to see revenue and spend next to every test.
        </p>
      </div>

      <ul className="flex flex-col rounded-xl bg-card shadow-[0_0_0_1px_var(--border),0_1px_2px_rgb(0_0_0/0.04)]">
        {connectors.map((row) => {
          const meta = CONNECTOR_META[row.id]
          return (
            <li
              key={row.id}
              className="flex items-center gap-3.5 border-b border-ui-hairline px-4 py-[18px] last:border-b-0 sm:px-5"
            >
              <BrandIcon brand={meta.brand} className="size-7 shrink-0" aria-hidden />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="text-sm font-medium leading-[18px] text-foreground">{meta.name}</p>
                <p className="text-[13px] leading-4 text-muted-foreground">{meta.detail}</p>
              </div>
              {row.connected ? (
                <ConnectedPill />
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  disabled={row.connected === null || connectingId === row.id}
                  onClick={() => onConnect(row.id)}
                  aria-label={`Connect ${meta.name}`}
                  className="h-8 shrink-0 rounded-lg px-3.5 text-[13px] font-medium"
                >
                  {connectingId === row.id ? "Opening…" : "Connect"}
                </Button>
              )}
            </li>
          )
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-4">
        <Button
          type="button"
          onClick={onContinue}
          className="h-11 rounded-lg bg-[var(--onboarding-action,oklch(0.54_0.21_42))] px-5 text-sm font-medium text-white hover:bg-[var(--onboarding-action,oklch(0.54_0.21_42))] hover:opacity-90"
        >
          {continueLabel}
        </Button>
        <button
          type="button"
          onClick={onLater}
          className="min-h-6 text-sm leading-[18px] text-muted-foreground hover:text-foreground"
        >
          I'll do this later
        </button>
      </div>
    </section>
  )
}
